import { Component, computed, effect, input, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FirstLettersPipe } from '@core/pipes/first-letters.pipe';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ImageLoaderDirective } from '@shared/directives/image-loader.directive';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  imports: [FirstLettersPipe, ImageLoaderDirective, MatTooltipModule, NgxSkeletonLoaderModule, IconComponent],
  selector: 'app-icon-badge',
  templateUrl: './icon-badge.component.html',
})
export class IconBadgeComponent {
  private readonly defaultIconSize = 14 as const;

  public readonly content = input.required<string | null | undefined>();

  public readonly fallbackText = input<string | null | undefined>('');
  public readonly fallbackTextFontWeight = input<number>(700);
  public readonly color = input<string>('var(--shikenso-purple-base-40)');
  public readonly backgroundColor = input<string>('var(--shikenso-gray-base)');
  public readonly fallbackBackgroundColor = input<string>('var(--shikenso-gray-base)');
  public readonly ignoreIconColor = input<boolean>(false);
  public readonly borderRadius = input<number>(8);
  public readonly borderColor = input<string | null>(null);
  public readonly borderWidth = input<number>(0);
  public readonly alt = input<string>('Icon');
  public readonly size = input<number>(24);
  public readonly width = input<number | null>(null);
  public readonly maxHeight = input<number | null>(null);
  public readonly iconSize = input<number>(this.defaultIconSize);
  public readonly fontSize = input<number | null>(null);
  public readonly inverted = input<boolean>(false);
  public readonly shortenText = input<boolean>(true);
  public readonly isSocialBadge = input<boolean>(false);
  public readonly showAuthenticationIcon = input<boolean>(false);
  public readonly showFallbackTextAsTooltip = input<boolean>(false);
  public readonly fallbackTextTooltipPrefix = input<string>('');
  public readonly noMaskSize = input<boolean>(false);

  public readonly textTopPadding = computed(() =>
    (this.fontSize() ?? this.iconSize()) > this.defaultIconSize ? 2 : 0,
  );
  public readonly textSize = computed(() => this.fontSize() ?? this.iconSize());
  public readonly isIcon = computed(() => {
    const content = this.content();

    if (!content || content.trim() === '') {
      return true;
    }

    return content.includes('icons/');
  });
  // "Uploaded image" here means one of our own files (fetched via ImageLoaderDirective/
  // getImageUrlAndSize) rather than a static /icons/ path or an external URL rendered as-is -
  // isDisplayableFileId also recognizes a legacy base64 id (pre id-format-migration data that
  // never got backfilled, but that the backend's read endpoints still resolve), so those keep
  // displaying as real images instead of falling back to the text/fallback-icon state below.
  public readonly isNotUploadedImage = computed(() => {
    const content = this.content();

    if (!content || content.trim() === '') {
      return true;
    }

    return content.includes('icons/') || !ImageUtilitiesService.isDisplayableFileId(content);
  });

  public readonly processedImageUrl = computed(() => {
    const content = this.content();
    const targetSize = this.calculatedContainerWidth();

    if (!content || !this.isNotUploadedImage()) {
      return null;
    }

    if (content.includes('icons/') || ImageUtilitiesService.isDisplayableFileId(content)) {
      return content;
    }

    return ImageUtilitiesService.replaceImageDimensions(content, targetSize, targetSize);
  });

  public readonly shouldShowText = computed(() => {
    const content = this.content();

    if (!content || content.trim() === '') {
      return true;
    }

    return this.hasImageError() || this.isBlockedExternalImage();
  });

  public readonly calculatedBackgroundColor = computed(() =>
    this.shouldShowText() ? this.fallbackBackgroundColor() : this.backgroundColor(),
  );

  public readonly calculatedContainerWidth = computed(() => this.width() ?? this.size());
  public readonly calculatedContainerHeight = computed(() => this.width() ?? this.size());

  public readonly calculatedImageWidth = computed(() => this.width() ?? this.iconSize());
  public readonly calculatedImageHeight = computed(() => (this.width() ? null : this.iconSize()));

  public readonly calculatedIconSizeClasses = computed(() => `w-[${this.iconSize()}px] h-[${this.iconSize()}px]`);

  public readonly tooltipText = computed(() => {
    if (!this.showFallbackTextAsTooltip()) {
      return null;
    }

    if (this.fallbackText()) {
      return this.fallbackTextTooltipPrefix() + (this.fallbackText() ?? '');
    }

    return (this.content() ?? '') || null;
  });

  public readonly hasImageError = signal<boolean>(false);
  public readonly isImageLoading = signal<boolean>(false);

  public readonly transparentImage = ImageUtilitiesService.transparentImage;

  constructor() {
    effect(() => {
      if (this.content()) {
        this.hasImageError.set(false);
      }

      if (this.isNotUploadedImage()) {
        this.isImageLoading.set(false);
      } else {
        this.isImageLoading.set(true);
      }
    });
  }

  public readonly isBlockedExternalImage = computed(() => {
    const url = this.processedImageUrl();

    if (!url || this.isIcon() || !this.isNotUploadedImage()) {
      return false;
    }

    const isAbsolute = url.startsWith('http://') || url.startsWith('https://');

    if (!isAbsolute) {
      return false;
    }

    try {
      const { hostname } = new URL(url);

      if (!hostname) {
        return false;
      }

      return hostname.endsWith('fbcdn.net');
    } catch {
      return false;
    }
  });

  public onIconError(): void {
    if (!this.isNotUploadedImage()) {
      return;
    }

    this.hasImageError.set(true);
  }

  public onImageLoaded(): void {
    this.isImageLoading.set(false);
  }

  public onImageLoading(): void {
    this.isImageLoading.set(true);
  }

  public onImageError(): void {
    this.hasImageError.set(true);

    this.isImageLoading.set(false);
  }
}
